import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from '@app/pages/auth/shared/services/auth.service';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  private getJwtExpiryMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '='
      );
      const payload = JSON.parse(atob(padded));
      const expSeconds = Number(payload?.exp);
      return Number.isFinite(expSeconds) ? expSeconds * 1000 : null;
    } catch {
      return null;
    }
  }

  private isNearExpiry(token: string, skewMs: number): boolean {
    const expiryMs = this.getJwtExpiryMs(token);
    if (!expiryMs) {
      return false;
    }
    return Date.now() >= expiryMs - skewMs;
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const rawSession = localStorage.getItem('buslane-session');

    let accessToken: string | undefined;
    try {
      accessToken = rawSession
        ? JSON.parse(rawSession)?.access_token
        : undefined;
    } catch {
      accessToken = undefined;
    }

    if (!accessToken) {
      return next.handle(req);
    }

    const base = new URL(environment.apiRoute, window.location.origin);
    const target = new URL(req.url, window.location.origin);
    const basePath = base.pathname.replace(/\/$/, '');
    const shouldAttach = target.origin === base.origin &&
      (target.pathname === basePath || target.pathname.startsWith(`${basePath}/`));
    const isAuthEndpoint = target.pathname.startsWith(`${basePath}/auth/`);
    if (!shouldAttach || isAuthEndpoint) return next.handle(req);

    const attachAndHandle = (tokenToUse: string) => {
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (isAuthEndpoint || error.status !== 401) {
            return throwError(error);
          }

          return this.authService.refreshSession().pipe(
            switchMap((session) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${session.access_token}`,
                },
              });
              return next.handle(retryReq);
            })
          );
        })
      );
    };

    if (!isAuthEndpoint && this.isNearExpiry(accessToken, 60_000)) {
      return this.authService
        .refreshSession()
        .pipe(switchMap((session) => attachAndHandle(session.access_token)));
    }

    return attachAndHandle(accessToken);
  }
}
